import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy,
  OnInit, Output, SimpleChanges, ViewChild, ElementRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { WfSubTaskApi } from '../services/wf-sub-task-api.service';
import {
  CreateSubTaskRequest, InstanceComment, WfSubTask, WfSubTaskSubject
} from '../services/wf-runtime-types';

interface PickerItem {
  id: string;
  displayName?: string;
  email?: string;
}

/**
 * H.6-a — Universal Tasks panel.
 *
 * <p>Renders the list of sub-tasks on the current instance and gives
 * the current task owner an "Add task" affordance. Drops into every
 * layout template (officer-360, document-centric, dual-pane, etc.) via
 * the shared #formFields ng-template / legacy 2-col grid in the task
 * detail component.
 *
 * <p><b>Authorization model</b> (server-enforced; client mirrors for UX):
 * <ul>
 *   <li>Create: only when {@link canCreate} = true (parent passes this
 *       based on the user owning the current Flowable task)</li>
 *   <li>Tick complete: if the user IS the assignee (or in the group)
 *       OR is the creator of the sub-task</li>
 *   <li>Cancel: only the creator of the sub-task</li>
 * </ul>
 *
 * <p>Visual: collapsible card with a red badge showing pending count.
 * Overdue sub-tasks (past deadline) get an extra "Overdue" pill.
 */
@Component({
  selector: 'wf-sub-tasks-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="st-panel">
      <header class="st-header" (click)="expanded = !expanded">
        <span class="material-icons st-icon">forum</span>
        <span class="st-title">{{ label }}</span>
        <span class="st-badge" *ngIf="pendingCount > 0">{{ pendingCount }}</span>
        <span class="st-grow"></span>
        <span class="material-icons st-chevron"
              [class.up]="expanded">expand_more</span>
      </header>

      <div class="st-body" *ngIf="expanded">

        <!-- Empty state -->
        <div class="st-empty" *ngIf="!loading && tasks.length === 0 && !addFormOpen">
          <span class="material-icons">inbox</span>
          <div class="st-empty-text">No {{ label.toLowerCase() }} requests yet</div>
          <button *ngIf="canCreate" type="button" class="st-add-btn"
                  (click)="openAddForm()">
            <span class="material-icons">add</span>
            <span>{{ label }} someone</span>
          </button>
        </div>

        <!-- Loading -->
        <div class="st-loading" *ngIf="loading">
          <span class="material-icons spin">progress_activity</span>
          <span>Loading…</span>
        </div>

        <!-- Task list -->
        <ul class="st-list" *ngIf="!loading && tasks.length > 0">
          <li class="st-item"
              *ngFor="let t of tasks"
              [class.done]="isDone(t)"
              [class.overdue]="isOverdue(t)">
            <div class="st-item-body">
              <!-- Title + complete toggle on one header line -->
              <div class="st-item-title-row">
                <div class="st-item-title">
                  <span>{{ t.title }}</span>
                  <span class="st-pill st-pill-status" *ngIf="t.status === 'ESCALATED'">Escalated</span>
                  <span class="st-pill st-pill-overdue" *ngIf="isOverdue(t)">Overdue</span>
                </div>
                <button type="button"
                        class="st-complete-btn"
                        *ngIf="!isDone(t) && canComplete(t)"
                        (click)="onComplete(t)"
                        [title]="completeLabel(t)">
                  <span class="material-icons">{{ t.kind === 'CONSULT' ? 'task_alt' : 'mark_email_read' }}</span>
                  {{ completeLabel(t) }}
                </button>
                <span class="st-complete-done" *ngIf="isDone(t)" title="Completed">
                  <span class="material-icons">check_circle</span>
                </span>
              </div>
              <div class="st-item-meta">
                <span class="st-assignee">
                  <span class="material-icons"
                        [class.group]="t.assigneeType === 'GROUP'">{{
                    t.assigneeType === 'GROUP' ? 'group' : 'person'
                  }}</span>
                  {{ t.assigneeName || t.assigneeId }}
                </span>
                <span class="st-deadline" *ngIf="t.deadlineAt">
                  · Due {{ formatDate(t.deadlineAt) }}
                </span>
                <span class="st-by">
                  · by {{ t.createdByName || t.createdBy }}
                </span>
              </div>
              <div class="st-instructions" *ngIf="t.instructions">
                <span class="st-q" *ngIf="t.kind === 'CONSULT'" title="Question">Q</span>{{ t.instructions }}
              </div>
              <div class="st-completion" *ngIf="t.status === 'COMPLETED'">
                Completed {{ formatDate(t.completedAt!) }} by {{ t.completedBy }}
                <span *ngIf="t.completionNote"> — {{ t.completionNote }}</span>
              </div>

              <!-- R2.3 — consultation actions: open thread / re-consult -->
              <div class="st-item-actions">
                <button type="button" class="st-link"
                        (click)="toggleThread(t)">
                  <span class="material-icons">chat_bubble_outline</span>
                  {{ threadOpenId === t.id ? 'Hide discussion' : 'Discussion' }}
                  <span *ngIf="(threadComments[t.id]?.length || 0) > 0"> ({{ threadComments[t.id]?.length }})</span>
                </button>
                <button type="button" class="st-link"
                        *ngIf="canReconsult(t)"
                        (click)="openAddForm(t.id)">
                  <span class="material-icons">forum</span>
                  {{ label }} further
                </button>
              </div>

              <!-- R2.3 — consultation thread (task-scoped, add/reply) -->
              <div class="st-thread" *ngIf="threadOpenId === t.id">
                <div class="st-thread-loading" *ngIf="threadLoading">Loading…</div>
                <div class="st-thread-empty"
                     *ngIf="!threadLoading && (threadComments[t.id]?.length || 0) === 0">
                  No responses yet.
                </div>
                <div class="st-thread-msg" *ngFor="let c of threadComments[t.id]">
                  <div class="st-thread-head">
                    <b>{{ c.authorName || c.authorId }}</b>
                    <span class="st-thread-when">{{ formatDate(c.createdAt!) }}</span>
                  </div>
                  <div class="st-thread-body">{{ c.body }}</div>
                </div>
                <div class="st-thread-compose">
                  <textarea class="st-textarea" rows="2"
                            [(ngModel)]="threadDraft"
                            placeholder="Add or reply within this {{ label.toLowerCase() }}…"></textarea>
                  <button type="button" class="st-btn st-btn-primary"
                          [disabled]="threadPosting || !threadDraft.trim()"
                          (click)="postThreadComment(t)">Post</button>
                </div>
              </div>
            </div>
            <button class="st-cancel-btn"
                    *ngIf="canCancel(t)"
                    type="button"
                    (click)="onCancel(t)"
                    title="Withdraw this request (only you can do this as the creator)">
              <span class="material-icons">close</span>
            </button>
          </li>
        </ul>

        <!-- Add button when there are existing requests -->
        <button *ngIf="canCreate && tasks.length > 0 && !addFormOpen"
                type="button" class="st-add-btn-inline"
                (click)="openAddForm()">
          <span class="material-icons">add</span>
          <span>{{ label }} someone else</span>
        </button>

        <!-- Inline create form -->
        <div class="st-form" *ngIf="addFormOpen">
          <div class="st-reconsult-note" *ngIf="reconsultParentId">
            <span class="material-icons">subdirectory_arrow_right</span>
            Following up on an existing {{ label.toLowerCase() }}
          </div>
          <div class="st-form-row" *ngIf="showSubject">
            <label class="st-label">Subject <span class="req">*</span></label>
            <input type="text" class="st-input" [(ngModel)]="newTitle"
                   placeholder="e.g. Please advise on clause 4" maxlength="255">
          </div>
          <div class="st-form-row">
            <label class="st-label">Instructions</label>
            <textarea class="st-textarea" [(ngModel)]="newInstructions"
                      placeholder="Optional — details for the assignee" rows="2"></textarea>
          </div>

          <!-- Assignee -->
          <div class="st-form-row">
            <label class="st-label">Assign to <span class="req">*</span></label>
            <div class="st-pick">
              <!-- Consultation is user-only: the User/Group toggle is removed and
                   newAssigneeType stays 'USER' (set in openAddForm). -->
              <div class="st-search" #assigneeWrap>
                <div class="st-search-input"
                     [class.focused]="assigneeFocused"
                     (click)="assigneeInput.focus()">
                  <span class="st-chip" *ngIf="assigneePicked">
                    <span class="material-icons">{{
                      newAssigneeType === 'GROUP' ? 'group' : 'person'
                    }}</span>
                    <span>{{ assigneePicked.displayName || assigneePicked.id }}</span>
                    <button type="button" class="st-chip-x"
                            (click)="clearAssignee(); $event.stopPropagation()">×</button>
                  </span>
                  <input #assigneeInput type="text"
                         class="st-search-text"
                         [(ngModel)]="assigneeQuery"
                         (ngModelChange)="onAssigneeQueryChange()"
                         (focus)="assigneeFocused = true; onAssigneeQueryChange()"
                         (blur)="onAssigneeBlur()"
                         [placeholder]="assigneePicked ? '' : (newAssigneeType === 'GROUP' ? 'Type a group…' : 'Type a name…')"
                         [hidden]="!!assigneePicked">
                  <span class="material-icons st-search-icon">{{
                    assigneeSearching ? 'progress_activity' : 'search'
                  }}</span>
                </div>
                <div class="st-search-dropdown"
                     *ngIf="assigneeFocused && !assigneePicked && assigneeQuery.length > 0">
                  <div class="st-search-result"
                       *ngFor="let r of assigneeResults"
                       (mousedown)="selectAssignee(r)">
                    <div class="rn">{{ r.displayName || r.id }}</div>
                    <div class="re" *ngIf="r.email">{{ r.email }}</div>
                  </div>
                  <div class="st-search-empty"
                       *ngIf="!assigneeResults.length && !assigneeSearching">No matches</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Deadline + Escalation -->
          <div class="st-form-row" *ngIf="showDeadline">
            <label class="st-label">Deadline</label>
            <input type="datetime-local" class="st-input st-input-narrow"
                   [(ngModel)]="newDeadline" (change)="onDeadlineChange()">
          </div>

          <div class="st-form-row" *ngIf="newDeadline">
            <label class="st-label">Escalate to <span class="req">*</span></label>
            <div class="st-pick">
              <div class="st-pick-toggle">
                <label><input type="radio" name="escalationType"
                       [(ngModel)]="newEscalationType" value="USER"
                       (change)="resetEscalation()"> User</label>
                <label><input type="radio" name="escalationType"
                       [(ngModel)]="newEscalationType" value="GROUP"
                       (change)="resetEscalation()"> Group</label>
              </div>
              <div class="st-search" #escalationWrap>
                <div class="st-search-input"
                     [class.focused]="escalationFocused"
                     (click)="escalationInput.focus()">
                  <span class="st-chip" *ngIf="escalationPicked">
                    <span class="material-icons">{{
                      newEscalationType === 'GROUP' ? 'group' : 'person'
                    }}</span>
                    <span>{{ escalationPicked.displayName || escalationPicked.id }}</span>
                    <button type="button" class="st-chip-x"
                            (click)="clearEscalation(); $event.stopPropagation()">×</button>
                  </span>
                  <input #escalationInput type="text"
                         class="st-search-text"
                         [(ngModel)]="escalationQuery"
                         (ngModelChange)="onEscalationQueryChange()"
                         (focus)="escalationFocused = true; onEscalationQueryChange()"
                         (blur)="onEscalationBlur()"
                         [placeholder]="escalationPicked ? '' : (newEscalationType === 'GROUP' ? 'Type a group…' : 'Type a name…')"
                         [hidden]="!!escalationPicked">
                  <span class="material-icons st-search-icon">{{
                    escalationSearching ? 'progress_activity' : 'search'
                  }}</span>
                </div>
                <div class="st-search-dropdown"
                     *ngIf="escalationFocused && !escalationPicked && escalationQuery.length > 0">
                  <div class="st-search-result"
                       *ngFor="let r of escalationResults"
                       (mousedown)="selectEscalation(r)">
                    <div class="rn">{{ r.displayName || r.id }}</div>
                    <div class="re" *ngIf="r.email">{{ r.email }}</div>
                  </div>
                  <div class="st-search-empty"
                       *ngIf="!escalationResults.length && !escalationSearching">No matches</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Editable consult — off by default (read-only). When on, this person opens the document
               in the editor and edits it directly; otherwise they comment via the discussion. -->
          <div class="st-form-row st-allow-edit">
            <label class="st-allow-edit-lbl">
              <input type="checkbox" [(ngModel)]="newAllowEdit" name="allowEdit">
              <span>Allow editing the document</span>
            </label>
            <span class="st-allow-edit-hint">{{ newAllowEdit
              ? 'They can edit the document directly.'
              : 'Read-only — they respond via the discussion (default).' }}</span>
          </div>

          <div class="st-form-error" *ngIf="formError">{{ formError }}</div>

          <div class="st-form-actions">
            <button type="button" class="st-btn st-btn-ghost"
                    [disabled]="submitting"
                    (click)="cancelAddForm()">Cancel</button>
            <button type="button" class="st-btn st-btn-primary"
                    [disabled]="submitting || !canSubmit()"
                    (click)="createTask()">
              <span class="material-icons spin" *ngIf="submitting">progress_activity</span>
              <span>{{ submitting ? 'Sending…' : 'Send ' + label.toLowerCase() }}</span>
            </button>
          </div>
        </div>

      </div>
    </section>
  `,
  styles: [`
    :host { display: block; }
    .st-panel {
      background: #fff;
      border: 1px solid rgba(0,0,0,0.08);
      border-radius: 8px;
      overflow: hidden;
      margin: 12px 0;
    }
    .st-header {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 14px;
      cursor: pointer;
      font-size: 13px; font-weight: 500;
      color: rgba(0,0,0,0.85);
      user-select: none;
      transition: background 120ms ease;
    }
    .st-header:hover { background: rgba(0,0,0,0.025); }
    .st-icon { font-size: 18px; color: #244062; }
    .st-title { font-weight: 600; }
    .st-grow { flex: 1; }
    .st-badge {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 20px; height: 20px;
      padding: 0 6px;
      background: #dc3545; color: white;
      border-radius: 10px;
      font-size: 11px; font-weight: 600;
    }
    .st-chevron { font-size: 20px; color: rgba(0,0,0,0.45); transition: transform 200ms ease; }
    .st-chevron.up { transform: rotate(180deg); }

    .st-body {
      border-top: 1px solid rgba(0,0,0,0.06);
      padding: 8px 12px 10px;
    }
    .st-empty {
      display: flex; flex-direction: column; align-items: center;
      padding: 18px 12px;
      color: rgba(0,0,0,0.45);
      gap: 6px;
    }
    .st-empty .material-icons { font-size: 32px; opacity: 0.5; }
    .st-empty-text { font-size: 12px; }
    .st-loading { display: flex; align-items: center; gap: 8px; padding: 12px; color: rgba(0,0,0,0.55); font-size: 12px; }

    /* List */
    .st-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
    .st-item {
      display: flex; align-items: flex-start; gap: 8px;
      padding: 8px 10px;
      border-radius: 4px;
      transition: background 120ms ease;
    }
    .st-item:hover { background: #f8f9fc; }
    .st-item.done { opacity: 0.6; }
    .st-item.done .st-item-title > span:first-child { text-decoration: line-through; }
    .st-item.overdue { background: #fef3c7; }
    .st-item.overdue:hover { background: #fde8a4; }
    .st-check { width: 19px; height: 19px; cursor: pointer; accent-color: #1D9E75; flex: none; }
    .st-check:disabled { cursor: not-allowed; }
    .st-item-body { flex: 1; min-width: 0; font-size: 13px; }
    /* Title + complete toggle share one header line */
    .st-item-title-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 2px; }
    .st-item-title {
      display: flex; align-items: center; gap: 6px; min-width: 0;
      font-weight: 600; color: #1f2937; font-size: 13.5px;
    }
    .st-item-title > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .st-complete { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; white-space: nowrap; flex: none; }
    .st-complete.disabled { cursor: not-allowed; opacity: 0.6; }
    .st-complete-label { font-size: 11.5px; color: #15803d; font-weight: 600; }
    .st-complete-btn {
      display: inline-flex; align-items: center; gap: 5px; flex: none; white-space: nowrap;
      font-size: 11.5px; font-weight: 600; color: #fff; background: #1D9E75;
      border: none; border-radius: 6px; padding: 5px 10px; cursor: pointer;
    }
    .st-complete-btn:hover { background: #178a64; }
    .st-complete-btn .material-icons { font-size: 15px; }
    .st-complete-done { display: inline-flex; align-items: center; color: #15803d; flex: none; }
    .st-complete-done .material-icons { font-size: 18px; }
    .st-item-meta { color: #4b5563; font-size: 12px; }
    .st-assignee { display: inline-flex; align-items: center; gap: 3px; }
    .st-assignee .material-icons { font-size: 12px; }
    .st-assignee .material-icons.group { color: #5b21b6; }
    .st-deadline, .st-by { white-space: nowrap; }
    .st-instructions {
      margin-top: 4px; padding: 5px 8px;
      background: rgba(0,0,0,0.035); border-left: 2px solid rgba(0,0,0,0.14);
      border-radius: 0 4px 4px 0;
      color: #374151; font-size: 12.5px;
      white-space: pre-wrap;
    }
    .st-q {
      display: inline-flex; align-items: center; justify-content: center;
      width: 16px; height: 16px; margin-right: 6px;
      background: #7C3AED; color: #fff; border-radius: 50%;
      font-size: 10px; font-weight: 700; vertical-align: middle;
    }
    .st-completion { margin-top: 4px; color: #15803d; font-weight: 500; }

    /* R2.3 — consultation actions + thread */
    .st-item-actions { display: flex; gap: 12px; margin-top: 5px; }
    .st-link {
      display: inline-flex; align-items: center; gap: 3px;
      background: none; border: none; cursor: pointer;
      color: #244062; font-size: 11px; padding: 0;
    }
    .st-link:hover { text-decoration: underline; }
    .st-link .material-icons { font-size: 13px; }
    .st-thread {
      margin-top: 6px; padding: 8px;
      background: #f6f8fb; border: 1px solid #e3e6ec; border-radius: 6px;
    }
    .st-thread-loading, .st-thread-empty { color: #9ca3af; font-size: 11px; font-style: italic; }
    .st-thread-msg { padding: 4px 0; border-bottom: 1px solid rgba(0,0,0,0.05); }
    .st-thread-msg:last-of-type { border-bottom: none; }
    .st-thread-head { display: flex; gap: 6px; align-items: baseline; }
    .st-thread-head b { font-size: 12px; color: #111827; }
    .st-thread-when { font-size: 10.5px; color: #6b7280; }
    .st-thread-body { font-size: 12.5px; color: #374151; white-space: pre-wrap; margin-top: 1px; }
    .st-thread-compose { display: flex; gap: 6px; align-items: flex-end; margin-top: 6px; }
    .st-thread-compose .st-textarea { flex: 1; }
    .st-reconsult-note {
      display: flex; align-items: center; gap: 4px;
      margin-bottom: 8px; padding: 4px 8px;
      background: #eef2f7; border-radius: 4px;
      color: #244062; font-size: 11px;
    }
    .st-reconsult-note .material-icons { font-size: 14px; }

    .st-pill {
      display: inline-flex; align-items: center;
      padding: 1px 6px; border-radius: 10px;
      font-size: 10px; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .st-pill-overdue { background: #fee2e2; color: #991b1b; }
    .st-pill-status { background: #ede9fe; color: #5b21b6; }

    .st-cancel-btn {
      background: none; border: none; cursor: pointer; padding: 2px;
      color: rgba(0,0,0,0.4);
    }
    .st-cancel-btn:hover { color: #dc3545; }
    .st-cancel-btn .material-icons { font-size: 16px; }

    /* Add button */
    .st-add-btn, .st-add-btn-inline {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 5px 10px;
      background: white;
      border: 1px dashed rgba(36,64,98,0.4);
      color: #244062;
      border-radius: 4px;
      font-size: 12px; font-weight: 500;
      cursor: pointer;
      margin-top: 6px;
    }
    .st-add-btn:hover, .st-add-btn-inline:hover {
      background: rgba(36,64,98,0.04);
      border-style: solid;
    }
    .st-add-btn .material-icons, .st-add-btn-inline .material-icons { font-size: 14px; }
    .st-add-btn-inline { margin-top: 8px; }

    /* Form */
    .st-form {
      background: #f8f9fc;
      border: 1px solid #e3e6ec;
      border-radius: 6px;
      padding: 10px;
      margin-top: 8px;
      font-size: 12px;
    }
    .st-form-row { display: flex; flex-direction: column; gap: 3px; margin-bottom: 8px; }
    .st-allow-edit-lbl { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; color: #1f2937; cursor: pointer; }
    .st-allow-edit-lbl input { width: 15px; height: 15px; }
    .st-allow-edit-hint { font-size: 11.5px; color: #6b7280; margin-left: 22px; }
    .st-form-row:last-of-type { margin-bottom: 0; }
    .st-label { font-size: 11px; font-weight: 500; color: #4b5563; }
    .req { color: #dc3545; }
    .st-input, .st-textarea {
      padding: 5px 8px;
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 12px;
      width: 100%;
      box-sizing: border-box;
    }
    .st-textarea { font-family: inherit; resize: vertical; }
    .st-input:focus, .st-textarea:focus { outline: none; border-color: #244062; box-shadow: 0 0 0 2px rgba(36,64,98,0.1); }
    .st-input-narrow { max-width: 220px; }

    /* Picker */
    .st-pick { display: flex; flex-direction: column; gap: 4px; }
    .st-pick-toggle { display: flex; gap: 12px; font-size: 11px; color: #4b5563; }
    .st-pick-toggle label { display: inline-flex; align-items: center; gap: 3px; cursor: pointer; }
    .st-search { position: relative; }
    .st-search-input {
      display: flex; align-items: center; gap: 4px;
      padding: 4px 28px 4px 8px;
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      cursor: text;
      min-height: 28px;
      position: relative;
    }
    .st-search-input.focused { border-color: #244062; box-shadow: 0 0 0 2px rgba(36,64,98,0.1); }
    .st-search-icon { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-size: 14px; color: #9ca3af; pointer-events: none; }
    .st-search-text { flex: 1; border: none; outline: none; background: transparent; font-size: 12px; padding: 2px 0; min-width: 100px; }
    .st-chip {
      display: inline-flex; align-items: center; gap: 3px;
      padding: 2px 4px 2px 6px;
      background: #e8eef5; color: #244062;
      border-radius: 10px;
      font-size: 11px; line-height: 1;
    }
    .st-chip .material-icons { font-size: 12px; }
    .st-chip-x { background: none; border: none; cursor: pointer; color: inherit; font-size: 14px; line-height: 1; padding: 0 2px; opacity: 0.7; }
    .st-chip-x:hover { opacity: 1; }
    .st-search-dropdown {
      position: absolute; top: 100%; left: 0; right: 0;
      z-index: 100; margin-top: 2px;
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      max-height: 200px; overflow-y: auto;
    }
    .st-search-result { padding: 5px 9px; cursor: pointer; }
    .st-search-result:hover { background: #f3f4f6; }
    .st-search-result .rn { color: #111827; font-size: 12px; }
    .st-search-result .re { color: #6b7280; font-size: 10px; margin-top: 1px; }
    .st-search-empty { padding: 8px; text-align: center; color: #9ca3af; font-size: 11px; font-style: italic; }

    .st-form-error {
      padding: 6px 8px; margin-bottom: 6px;
      background: #fee2e2; color: #991b1b;
      border-radius: 4px; font-size: 11px;
    }
    .st-form-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 8px; }
    .st-btn {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 5px 12px;
      border-radius: 4px;
      font-size: 12px; font-weight: 500;
      cursor: pointer;
      border: 1px solid transparent;
    }
    .st-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .st-btn-ghost { background: white; color: rgba(0,0,0,0.7); border-color: rgba(0,0,0,0.2); }
    .st-btn-ghost:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
    .st-btn-primary { background: #244062; color: white; }
    .st-btn-primary:hover:not(:disabled) { background: #1a2f48; }
    .st-btn .material-icons { font-size: 14px; }

    .spin { animation: st-spin 1s linear infinite; display: inline-block; }
    @keyframes st-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
  `]
})
export class WfSubTasksPanelComponent implements OnInit, OnChanges, OnDestroy {

  @Input() instanceId!: number;
  /** True when the current user owns the parent task — controls visibility of "Add task". */
  @Input() canCreate = false;
  /** Current logged-in username — used to decide completer / canceller authority on the client. */
  @Input() currentUsername = '';
  /** Roles/groups of the current user — used to enable completion of group-assigned tasks. */
  @Input() userRoles: string[] = [];
  /** R2.3 — panel/button label (e.g. "Consult"). Blank = "Consult". */
  @Input() label = 'Consult';
  /** Completion button labels — configurable. FYI/copy tasks read "Mark As Read";
   *  a consult task reads "Mark as Complete". */
  @Input() fyiCompleteLabel = 'Mark As Read';
  @Input() consultCompleteLabel = 'Mark as Complete';
  /** R2.3 — open expanded (used when hosted in the My Work consultation modal). */
  @Input() startExpanded = false;
  /** R3 — per-step config: show the Subject / Deadline fields on the create form (default true). */
  @Input() showSubject = true;
  @Input() showDeadline = true;

  @Output() pendingCountChange = new EventEmitter<number>();

  tasks: WfSubTask[] = [];
  loading = false;
  expanded = false;

  // ── R2.3 consultation thread + re-consult state ────────────────────────
  reconsultParentId: number | null = null;
  threadOpenId: number | null = null;
  threadLoading = false;
  threadPosting = false;
  threadDraft = '';
  threadComments: Record<number, InstanceComment[]> = {};

  // ── Add-form state ─────────────────────────────────────────────────────
  addFormOpen = false;
  submitting = false;
  formError: string | null = null;

  newTitle = '';
  newInstructions = '';
  newAllowEdit = false;   // editable consult — off by default (read-only)

  /** Completion-button label: a CONSULT task is "Mark as Complete"; an FYI/copy
   *  task (read-only) is "Mark As Read". Both configurable via @Input. */
  completeLabel(t: WfSubTask): string {
    return t?.kind === 'CONSULT' ? this.consultCompleteLabel : this.fyiCompleteLabel;
  }
  newAssigneeType: WfSubTaskSubject = 'USER';
  newDeadline = '';
  newEscalationType: WfSubTaskSubject = 'USER';

  // Picker state — assignee
  assigneeQuery = '';
  assigneeResults: PickerItem[] = [];
  assigneePicked: PickerItem | null = null;
  assigneeSearching = false;
  assigneeFocused = false;
  private assigneeTimer: any = null;
  private assigneeBlurTimer: any = null;

  // Picker state — escalation
  escalationQuery = '';
  escalationResults: PickerItem[] = [];
  escalationPicked: PickerItem | null = null;
  escalationSearching = false;
  escalationFocused = false;
  private escalationTimer: any = null;
  private escalationBlurTimer: any = null;

  @ViewChild('assigneeInput') assigneeInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('escalationInput') escalationInputRef?: ElementRef<HTMLInputElement>;

  get pendingCount(): number {
    return this.tasks.filter(t => t.status === 'PENDING' || t.status === 'ESCALATED').length;
  }

  constructor(private api: WfSubTaskApi) {}

  ngOnInit(): void {
    if (this.startExpanded) this.expanded = true;
    this.reload();
  }

  ngOnChanges(c: SimpleChanges): void {
    if (c['instanceId'] && !c['instanceId'].firstChange) this.reload();
  }

  ngOnDestroy(): void {
    if (this.assigneeTimer)  clearTimeout(this.assigneeTimer);
    if (this.escalationTimer) clearTimeout(this.escalationTimer);
    if (this.assigneeBlurTimer)  clearTimeout(this.assigneeBlurTimer);
    if (this.escalationBlurTimer) clearTimeout(this.escalationBlurTimer);
  }

  // ── Data loading ───────────────────────────────────────────────────────

  private reload(): void {
    if (!this.instanceId) return;
    this.loading = true;
    this.api.listByInstance(this.instanceId).subscribe({
      next: tasks => {
        this.tasks = tasks || [];
        this.loading = false;
        this.pendingCountChange.emit(this.pendingCount);
      },
      error: err => {
        console.error('[sub-tasks] list failed', err);
        this.tasks = [];
        this.loading = false;
        this.pendingCountChange.emit(0);
      }
    });
  }

  // ── Authorization (client mirror of backend rules) ─────────────────────

  canComplete(t: WfSubTask): boolean {
    if (t.status === 'COMPLETED' || t.status === 'CANCELLED') return false;
    if (this.currentUsername === t.createdBy) return true;
    if (t.assigneeType === 'USER' && this.currentUsername === t.assigneeId) return true;
    if (t.assigneeType === 'GROUP' && this.userRoles.includes(t.assigneeId)) return true;
    return false;
  }

  canCancel(t: WfSubTask): boolean {
    return (t.status === 'PENDING' || t.status === 'ESCALATED')
        && this.currentUsername === t.createdBy;
  }

  isDone(t: WfSubTask): boolean {
    return t.status === 'COMPLETED' || t.status === 'CANCELLED';
  }

  isOverdue(t: WfSubTask): boolean {
    if (t.status !== 'PENDING' || !t.deadlineAt) return false;
    return new Date(t.deadlineAt).getTime() < Date.now();
  }

  formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  // ── Actions on existing tasks ──────────────────────────────────────────

  onComplete(t: WfSubTask): void {
    if (!this.canComplete(t)) return;
    this.api.complete(t.id).subscribe({
      next: updated => {
        this.replaceTask(updated);
        this.pendingCountChange.emit(this.pendingCount);
      },
      error: err => console.error('[sub-tasks] complete failed', err)
    });
  }

  onCancel(t: WfSubTask): void {
    if (!this.canCancel(t)) return;
    if (!confirm(`Withdraw task "${t.title}"? It will be removed from ${t.assigneeName || t.assigneeId}'s inbox.`)) return;
    this.api.cancel(t.id).subscribe({
      next: updated => {
        this.replaceTask(updated);
        this.pendingCountChange.emit(this.pendingCount);
      },
      error: err => console.error('[sub-tasks] cancel failed', err)
    });
  }

  private replaceTask(updated: WfSubTask): void {
    this.tasks = this.tasks.map(t => t.id === updated.id ? updated : t);
  }

  // ── R2.3 consultation thread + re-consult ──────────────────────────────

  /** The consulted person (assignee) may consult further while it's still open. */
  canReconsult(t: WfSubTask): boolean {
    if (t.status === 'COMPLETED' || t.status === 'CANCELLED') return false;
    if (t.assigneeType === 'USER' && this.currentUsername === t.assigneeId) return true;
    if (t.assigneeType === 'GROUP' && this.userRoles.includes(t.assigneeId)) return true;
    // The original requester can also branch a further consultation.
    return this.currentUsername === t.createdBy;
  }

  toggleThread(t: WfSubTask): void {
    if (this.threadOpenId === t.id) { this.threadOpenId = null; return; }
    this.threadOpenId = t.id;
    this.threadDraft = '';
    this.loadThread(t.id);
  }

  private loadThread(id: number): void {
    this.threadLoading = true;
    this.api.listComments(id).subscribe({
      next: cs => { this.threadComments[id] = cs || []; this.threadLoading = false; },
      error: () => { this.threadComments[id] = this.threadComments[id] || []; this.threadLoading = false; }
    });
  }

  postThreadComment(t: WfSubTask): void {
    const body = this.threadDraft.trim();
    if (!body || this.threadPosting) return;
    this.threadPosting = true;
    this.api.addComment(t.id, body).subscribe({
      next: c => {
        this.threadComments[t.id] = [...(this.threadComments[t.id] || []), c];
        this.threadDraft = '';
        this.threadPosting = false;
      },
      error: () => { this.threadPosting = false; }
    });
  }

  // ── Add form ───────────────────────────────────────────────────────────

  openAddForm(parentId?: number): void {
    this.reconsultParentId = parentId ?? null;
    this.addFormOpen = true;
    this.formError = null;
    this.newTitle = '';
    this.newInstructions = '';
    this.newAllowEdit = false;
    this.newAssigneeType = 'USER';
    this.newDeadline = '';
    this.newEscalationType = 'USER';
    this.assigneeQuery = '';
    this.assigneeResults = [];
    this.assigneePicked = null;
    this.escalationQuery = '';
    this.escalationResults = [];
    this.escalationPicked = null;
  }

  cancelAddForm(): void {
    this.addFormOpen = false;
  }

  canSubmit(): boolean {
    if (this.showSubject && !this.newTitle.trim()) return false;
    if (!this.assigneePicked) return false;
    if (this.newDeadline && !this.escalationPicked) return false;
    return true;
  }

  onDeadlineChange(): void {
    // If deadline cleared, drop escalation
    if (!this.newDeadline) {
      this.escalationPicked = null;
      this.escalationQuery = '';
    }
  }

  createTask(): void {
    if (!this.canSubmit() || !this.assigneePicked) return;
    this.submitting = true;
    this.formError = null;

    const req: CreateSubTaskRequest = {
      title: this.newTitle.trim() || this.label || 'Consultation',
      instructions: this.newInstructions.trim() || undefined,
      kind: 'CONSULT',
      allowEdit: this.newAllowEdit,
      parentSubTaskId: this.reconsultParentId ?? undefined,
      assigneeType: this.newAssigneeType,
      assigneeId: this.assigneePicked.id,
      assigneeName: this.assigneePicked.displayName || this.assigneePicked.id,
      deadlineAt: this.newDeadline ? new Date(this.newDeadline).toISOString() : undefined,
      escalationType: this.newDeadline && this.escalationPicked ? this.newEscalationType : undefined,
      escalationId: this.newDeadline && this.escalationPicked ? this.escalationPicked.id : undefined,
      escalationName: this.newDeadline && this.escalationPicked
        ? (this.escalationPicked.displayName || this.escalationPicked.id) : undefined
    };

    this.api.create(this.instanceId, req).subscribe({
      next: created => {
        this.tasks = [created, ...this.tasks];
        this.addFormOpen = false;
        this.reconsultParentId = null;
        this.submitting = false;
        this.pendingCountChange.emit(this.pendingCount);
      },
      error: err => {
        this.submitting = false;
        this.formError = err?.error?.message || err?.message || 'Failed to send ' + this.label.toLowerCase();
      }
    });
  }

  // ── Picker mechanics (shared user/group search) ────────────────────────

  onAssigneeQueryChange(): void {
    if (this.assigneeTimer) clearTimeout(this.assigneeTimer);
    this.assigneeTimer = setTimeout(() => this.searchAssignee(), 250);
  }
  onEscalationQueryChange(): void {
    if (this.escalationTimer) clearTimeout(this.escalationTimer);
    this.escalationTimer = setTimeout(() => this.searchEscalation(), 250);
  }

  private async searchAssignee(): Promise<void> {
    const q = this.assigneeQuery.trim();
    if (q.length < 1) { this.assigneeResults = []; return; }
    this.assigneeSearching = true;
    try {
      this.assigneeResults = await this.fetchSearch(this.newAssigneeType, q);
    } catch {
      this.assigneeResults = [];
    } finally {
      this.assigneeSearching = false;
    }
  }

  private async searchEscalation(): Promise<void> {
    const q = this.escalationQuery.trim();
    if (q.length < 1) { this.escalationResults = []; return; }
    this.escalationSearching = true;
    try {
      this.escalationResults = await this.fetchSearch(this.newEscalationType, q);
    } catch {
      this.escalationResults = [];
    } finally {
      this.escalationSearching = false;
    }
  }

  private fetchSearch(kind: WfSubTaskSubject, term: string): Promise<PickerItem[]> {
    // Uses the injected API (HttpClient + AuthenticationService token) rather
    // than window globals, so the picker works on the standalone My Work modal
    // too — where the Form.io renderer's __skyplusGetToken is not published.
    return firstValueFrom(this.api.searchDirectory(kind, term));
  }

  selectAssignee(item: PickerItem): void {
    this.assigneePicked = item;
    this.assigneeQuery = '';
    this.assigneeResults = [];
    if (this.assigneeBlurTimer) clearTimeout(this.assigneeBlurTimer);
    this.assigneeFocused = false;
  }

  selectEscalation(item: PickerItem): void {
    this.escalationPicked = item;
    this.escalationQuery = '';
    this.escalationResults = [];
    if (this.escalationBlurTimer) clearTimeout(this.escalationBlurTimer);
    this.escalationFocused = false;
  }

  clearAssignee(): void {
    this.assigneePicked = null;
    setTimeout(() => this.assigneeInputRef?.nativeElement?.focus(), 0);
  }
  clearEscalation(): void {
    this.escalationPicked = null;
    setTimeout(() => this.escalationInputRef?.nativeElement?.focus(), 0);
  }

  resetAssignee(): void { this.assigneePicked = null; this.assigneeQuery = ''; this.assigneeResults = []; }
  resetEscalation(): void { this.escalationPicked = null; this.escalationQuery = ''; this.escalationResults = []; }

  onAssigneeBlur(): void {
    this.assigneeBlurTimer = setTimeout(() => { this.assigneeFocused = false; }, 150);
  }
  onEscalationBlur(): void {
    this.escalationBlurTimer = setTimeout(() => { this.escalationFocused = false; }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    const t = e.target as HTMLElement;
    if (!t.closest?.('wf-sub-tasks-panel')) {
      this.assigneeFocused = false;
      this.escalationFocused = false;
    }
  }
}